// Zurich-matter billing rule (Tyson Mendes only).
//
// Rule: 0.05 is the absolute minimum (≤3 minutes); above that, units snap
// to the standard 0.1 grid. Valid values: { 0.05, 0.1, 0.2, 0.3, ... }.
//
// All other firms — and all non-Zurich matters under TM — use the standard
// 0.1 grid with no 0.05 minimum.

import { isTysonMendes } from "./firm";

const ZURICH_MIN = 0.05;
const ZURICH_MIN_SECONDS = 180; // 3 minutes
const STANDARD_STEP = 0.1;

/** True only for TM matters whose client name contains "zurich" (case-insensitive). */
export function isZurichMatter(client: string | undefined | null): boolean {
  if (!isTysonMendes()) return false;
  if (!client) return false;
  return client.toLowerCase().includes("zurich");
}

/** Round seconds → valid units value for the given matter. */
export function roundUnitsForMatter(
  seconds: number,
  client: string | undefined | null,
): number {
  if (seconds <= 0) return 0;
  const hours = seconds / 3600;
  if (isZurichMatter(client) && seconds <= ZURICH_MIN_SECONDS) {
    return ZURICH_MIN;
  }
  return parseFloat((Math.ceil(hours * 10) / 10).toFixed(1));
}

/** Validate a units value typed/edited by the user for the given matter. */
export function isValidUnitsForMatter(
  units: number,
  client: string | undefined | null,
): boolean {
  if (!Number.isFinite(units) || units < 0) return false;
  if (units === 0) return false; // snap handles zero; callers should use snapUnitsForMatter
  const isZurich = isZurichMatter(client);
  if (isZurich && units === ZURICH_MIN) return true;
  if (isZurich && units < STANDARD_STEP) return false;
  return Math.abs(units * 10 - Math.round(units * 10)) < 1e-9;
}

/**
 * Snap an arbitrary units value to the nearest valid value (used on blur and matter switch).
 *
 * Zurich rule: only values representing ≤3 minutes (≤ 0.05 hours) snap to 0.05.
 * Anything above 3 minutes — even 0.06 hours (3.6 min) — snaps up to the standard
 * 0.1 grid, mirroring the seconds-based `roundUnitsForMatter` rule.
 */
export function snapUnitsForMatter(
  units: number,
  client: string | undefined | null,
): number {
  if (units <= 0) return isZurichMatter(client) ? ZURICH_MIN : STANDARD_STEP;
  if (isZurichMatter(client) && units <= ZURICH_MIN) return ZURICH_MIN;
  return parseFloat((Math.ceil(units * 10) / 10).toFixed(1));
}

/** Stepper helper: increment current units to the next valid value. */
export function stepUnitsUp(
  current: number,
  client: string | undefined | null,
): number {
  if (isZurichMatter(client) && current < STANDARD_STEP) return STANDARD_STEP;
  return parseFloat((current + STANDARD_STEP).toFixed(1));
}

/** Stepper helper: decrement current units to the previous valid value. */
export function stepUnitsDown(
  current: number,
  client: string | undefined | null,
): number {
  if (isZurichMatter(client)) {
    if (current > STANDARD_STEP) return parseFloat((current - STANDARD_STEP).toFixed(1));
    return ZURICH_MIN;
  }
  return Math.max(STANDARD_STEP, parseFloat((current - STANDARD_STEP).toFixed(1)));
}

export const ZURICH_UNITS_ERROR =
  "For Zurich matters, units must be 0.05 or a multiple of 0.10";
export const STANDARD_UNITS_ERROR = "Units must be a multiple of 0.10";
